import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Button,
  useTheme,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  Skeleton,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, subDays, addDays } from "date-fns";
import { tournamentApi } from "../../../services/api";
import { useSnackbar } from "notistack";
import type { Tournament } from "../../../models";
import TournamentDetailsDrawer from "./components/TournamentDetailsDrawer";
import {
  CRICKET_XTRACT_PLATFORM_ICONS,
  CRICKET_XTRACT_PLATFORMS,
} from "../cricketPlatforms";
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SportsIcon from '@mui/icons-material/Sports';

// Platform icon mapping (cricket shared; football/basketball extras below)
const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  ...CRICKET_XTRACT_PLATFORM_ICONS,
  fotmob: <Avatar sx={{ width: 24, height: 24, bgcolor: "#f57c00", fontSize: 14 }}>FM</Avatar>,
};

const CRICKET_PLATFORMS = CRICKET_XTRACT_PLATFORMS;

const FOOTBALL_PLATFORMS = [
  { id: "fotmob", name: "FotMob", visible: true },
  { id: "d11", name: "Dream11", visible: true },
  { id: "v11", name: "Vision11", visible: true },
  { id: "c11", name: "My11Circle", visible: false },
  { id: "mt11", name: "MyTeam11", visible: false },
  { id: "f11", name: "MyFab11", visible: false },
];

const BASKETBALL_PLATFORMS = [
  { id: "v11", name: "Vision11", visible: true },
];

type SportType = "cricket" | "football" | "kabaddi" | "basketball";
const getPlatforms = (sport: SportType) => {
  if (sport === "cricket") return CRICKET_PLATFORMS;
  if (sport === "football") return FOOTBALL_PLATFORMS;
  if (sport === "basketball") return BASKETBALL_PLATFORMS;
  return [];
};

const SPORT_ICONS: Record<string, React.ReactNode> = {
  cricket: <SportsCricketIcon fontSize="small" sx={{ color: '#1976d2' }} />,
  football: <SportsSoccerIcon fontSize="small" sx={{ color: '#43a047' }} />,
  kabaddi: <SportsKabaddiIcon fontSize="small" sx={{ color: '#fbc02d' }} />,
  basketball: <SportsBasketballIcon fontSize="small" sx={{ color: '#8e24aa' }} />,
};

const MODES = [
  { value: "recent", label: "Recent" },
  { value: "ongoing", label: "Ongoing" },
  { value: "upcoming", label: "Upcoming" },
];

const Tournaments: React.FC = () => {
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState<Date>(addDays(new Date(), 7));
  const [selectedSport, setSelectedSport] = useState<
    "cricket" | "football" | "kabaddi" | "basketball"
  >("cricket");
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [visiblePlatforms, setVisiblePlatforms] = useState<{ [key: string]: boolean }>({});
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [mode, setMode] = useState<string>("recent");
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [addEspnDialogOpen, setAddEspnDialogOpen] = useState(false);
  const [espnObjectId, setEspnObjectId] = useState('');
  const [espnLoading, setEspnLoading] = useState(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      setRefreshing(true);
      let response;
      if (mode === "ongoing") {
        response = await tournamentApi.getActive(selectedSport);
      } else if (mode === "upcoming") {
        response = await tournamentApi.getUpcoming(selectedSport);
      } else {
        response = await tournamentApi.getRecent(selectedSport);
      }
      setTournaments(response.data || []);
      // Initialize visible state for all platforms
      const initialVisibleState = getPlatforms(selectedSport).reduce((acc, platform) => {
        acc[platform.id] = platform.visible;
        return acc;
      }, {} as { [key: string]: boolean });
      setVisiblePlatforms(initialVisibleState);
    } catch (error) {
      console.error("Error fetching tournaments:", error);
      enqueueSnackbar("Error fetching tournaments", { variant: "error" });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTournaments();
    // eslint-disable-next-line
  }, [startDate, endDate, selectedSport, mode]);

  const togglePlatform = (platformId: string) => {
    setVisiblePlatforms((prev) => ({
      ...prev,
      [platformId]: !prev[platformId],
    }));
  };

  const toggleAllPlatforms = () => {
    const allVisible = Object.values(visiblePlatforms).every((v) => v);
    const newState = getPlatforms(selectedSport).reduce((acc, platform) => {
      acc[platform.id] = !allVisible;
      return acc;
    }, {} as { [key: string]: boolean });
    setVisiblePlatforms(newState);
  };

  const getTournamentsByPlatform = (platformId: string) => {
    return tournaments
      .filter((t) => t.platform === platformId)
      .filter((t) => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
          t.name.toLowerCase().includes(query) ||
          (t.long_name && t.long_name.toLowerCase().includes(query)) ||
          (t.format && t.format.toLowerCase().includes(query))
        );
      })
      .sort(
        (a, b) =>
          new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      );
  };

  const handleTournamentClick = (tournament: Tournament) => {
    setSelectedTournament(tournament);
  };

  const handleCloseDrawer = () => {
    setSelectedTournament(null);
  };

  const handleAddEspn = async () => {
    if (!espnObjectId) return;
    setEspnLoading(true);
    try {
      await tournamentApi.fetchEspnById(Number(espnObjectId));
      enqueueSnackbar('ESPN Tournament added successfully!', { variant: 'success' });
      setAddEspnDialogOpen(false);
      setEspnObjectId('');
      fetchTournaments();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.error || 'Failed to add ESPN tournament', { variant: 'error' });
    } finally {
      setEspnLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchTournaments();
  };

  // Determine tournament status for chip
  const getTournamentStatus = (t: Tournament): { label: string; color: string } => {
    const now = new Date();
    const start = new Date(t.start_date);
    const end = new Date(t.end_date);
    if (now < start) return { label: "Upcoming", color: "info" };
    if (now > end) return { label: "Completed", color: "default" };
    return { label: "Ongoing", color: "success" };
  };

  // --- UI ---
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        {/* Add ESPN Tournament Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button variant="contained" color="primary" onClick={() => setAddEspnDialogOpen(true)}>
            Add ESPN Tournament by ID
          </Button>
        </Box>
        {/* Add ESPN Tournament Dialog */}
        <Dialog open={addEspnDialogOpen} onClose={() => setAddEspnDialogOpen(false)}>
          <DialogTitle>Add ESPN Tournament by Object ID</DialogTitle>
          <DialogContent>
            <TextField
              label="ESPN Object ID"
              value={espnObjectId}
              onChange={e => setEspnObjectId(e.target.value)}
              fullWidth
              autoFocus
              type="number"
              margin="dense"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddEspnDialogOpen(false)} disabled={espnLoading}>Cancel</Button>
            <Button onClick={handleAddEspn} variant="contained" color="primary" disabled={!espnObjectId || espnLoading}>
              {espnLoading ? <CircularProgress size={18} /> : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Slim Top Bar */}
        <Paper
          sx={{
            p: { xs: 1, sm: 1.5 },
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
            position: "sticky",
            top: 60,
            zIndex: 2,
            backgroundColor: "background.paper",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            sx={{ flexWrap: "wrap" }}
          >
            {/* Left: Title & Mode Switch */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
                Tournaments
              </Typography>
              <Tooltip title="Refresh tournaments">
                <IconButton 
                  onClick={handleRefresh}
                  disabled={refreshing}
                  size="small"
                  sx={{ 
                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tabs
                value={mode}
                onChange={(_, v) => setMode(v)}
                variant="standard"
                sx={{ minHeight: 32, height: 32 }}
                TabIndicatorProps={{ style: { height: 2 } }}
              >
                {MODES.map((m) => (
                  <Tab
                    key={m.value}
                    value={m.value}
                    label={m.label}
                    sx={{ minHeight: 32, height: 32, fontSize: 14, px: 2 }}
                  />
                ))}
              </Tabs>
            </Stack>

            {/* Center: Date & Sport */}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Search tournaments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <DatePicker
                label="Start"
                value={startDate}
                onChange={(newValue: Date | null) => newValue && setStartDate(newValue)}
                slotProps={{ textField: { size: "small", sx: { minWidth: 110 } } }}
              />
              <DatePicker
                label="End"
                value={endDate}
                onChange={(newValue: Date | null) => newValue && setEndDate(newValue)}
                slotProps={{ textField: { size: "small", sx: { minWidth: 110 } } }}
              />
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Sport</InputLabel>
                <Select
                  value={selectedSport}
                  label="Sport"
                  onChange={(e) => setSelectedSport(e.target.value as typeof selectedSport)}
                >
          <MenuItem value="cricket">Cricket</MenuItem>
          <MenuItem value="football">Football</MenuItem>
          <MenuItem value="basketball">Basketball</MenuItem>
          {/* <MenuItem value="kabaddi">Kabaddi</MenuItem> */}
                </Select>
              </FormControl>
            </Stack>

            {/* Right: Platform toggles */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                onClick={toggleAllPlatforms}
                size="small"
                sx={{ minWidth: 80 }}
              >
                {Object.values(visiblePlatforms).every((v) => v)
                  ? "Hide All"
                  : "Show All"}
              </Button>
              {getPlatforms(selectedSport).map((platform) => (
                <Tooltip key={platform.id} title={platform.name} arrow>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={visiblePlatforms[platform.id]}
                        onChange={() => togglePlatform(platform.id)}
                        size="small"
                        color="primary"
                        inputProps={{ 'aria-label': `${platform.name} toggle` }}
                      />
                    }
                    label={PLATFORM_ICONS[platform.id]}
                    labelPlacement="top"
                    sx={{ m: 0, minWidth: 36 }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Stack>
        </Paper>

        {/* Tournament Cards by Platform */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            pb: 2,
            minHeight: 320,
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.2)"
                  : "rgba(0, 0, 0, 0.2)",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.3)"
                    : "rgba(0, 0, 0, 0.3)",
              },
            },
          }}
        >
          {getPlatforms(selectedSport).map((platform) => {
            const platformTournaments = getTournamentsByPlatform(platform.id);
            if (!visiblePlatforms[platform.id]) return null;
            return (
              <Paper
                key={platform.id}
                sx={{
                  minWidth: 320,
                  maxWidth: 340,
                  height: "fit-content",
                  maxHeight: "calc(100vh - 200px)",
                  display: "flex",
                  flexDirection: "column",
                  p: 0,
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.02)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {PLATFORM_ICONS[platform.id]}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {platform.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                    {platformTournaments.length} tournaments
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1,
                    overflowY: "auto",
                    minHeight: 220,
                    maxHeight: "calc(100vh - 260px)",
                  }}
                >
                  <Stack spacing={1}>
                    {loading
                      ? Array.from({ length: 4 }).map((_, i) => (
                          <Skeleton
                            key={i}
                            variant="rectangular"
                            height={70}
                            sx={{ borderRadius: 2 }}
                          />
                        ))
                      : platformTournaments.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                          No tournaments found.
                        </Typography>
                      ) : (
                        platformTournaments.map((tournament) => {
                          const status = getTournamentStatus(tournament);
                          return (
                            <Card
                              key={tournament.id}
                              sx={{
                                mb: 0.5,
                                border: !tournament.series_id ? "1px solid" : "none",
                                borderColor: "error.main",
                                cursor: "pointer",
                                boxShadow: 1,
                                borderRadius: 2,
                                transition: 'box-shadow 0.2s',
                                '&:hover': { boxShadow: 4, bgcolor: 'action.hover' },
                                p: 0,
                              }}
                              onClick={() => handleTournamentClick(tournament)}
                              aria-label={`View details for ${tournament.name}`}
                            >
                              <CardContent sx={{ p: 1.2, '&:last-child': { pb: 1.2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Chip
                                    label={status.label}
                                    color={status.color as any}
                                    size="small"
                                    sx={{ fontWeight: 500, fontSize: 11, height: 22 }}
                                    variant={status.color === 'default' ? 'outlined' : 'filled'}
                                  />
                                  <Box sx={{ flexGrow: 1 }} />
                                  {SPORT_ICONS[tournament.sport || 'cricket'] || <SportsIcon fontSize="small" />}
                                </Box>
                                <Typography
                                  variant="subtitle2"
                                  component="div"
                                  sx={{
                                    fontWeight: 500,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    fontSize: 13,
                                  }}
                                >
                                  {tournament.long_name?? tournament.name} {tournament.format ? `- ${tournament.format}` : ''}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', mt: 0.2, mb: 0.2 }}
                                >
                                  {format(new Date(tournament.start_date), "MMM dd, yyyy")} - {format(new Date(tournament.end_date), "MMM dd, yyyy")}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', fontSize: 11 }}
                                >
                                  Id: {tournament.platform_id} {tournament.series_id ? `| Series: ${tournament.series_id}` : ''} {tournament.extra_info?.primary_league_id ? `| PL: ${tournament.extra_info.primary_league_id}` : ''}
                                </Typography>
                              </CardContent>
                            </Card>
                          );
                        })
                      )}
                  </Stack>
                </Box>
              </Paper>
            );
          })}
        </Box>

        <TournamentDetailsDrawer
          open={!!selectedTournament}
          onClose={handleCloseDrawer}
          tournament={selectedTournament}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default Tournaments;
